import { GeneratedCV } from '@cibli/shared';
import { useMicdropState } from '@micdrop/react';
import { Micdrop, MicdropConversationMessage, MicdropState, Speaker } from '@micdrop/web';
import { OnnxSileroModel } from '@micdrop/web/silero';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useInterviewContext } from '@/features/common/contexts/interview.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import { getCvExemple } from '@/features/cv/CV_exemple';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { resolveWsUrl } from '@/utils/resolveWsUrl';

// eslint-disable-next-line react-refresh/only-export-components
export enum INTERVIEW_STEPS {
  INTRO = 0,
  INTERVIEW = 1,
  PHOTO = 2,
  SUMMARY = 3,
}

interface InterviewFlowContextType {
  step: INTERVIEW_STEPS;
  startInterview: (simulationScriptId?: string) => void;
  restartWithSimulation: (simulationScriptId: string) => void;
  goToSummary: () => void;
  lastMessage?: string;
  generatedCV?: GeneratedCV;
  setGeneratedCV?: (cv: GeneratedCV) => void;
  isCVGenerating: boolean;
  completeDevInterview?: () => void;
  /** Current sub-screen label within SUMMARY (e.g. "Choix du modèle CV"). */
  summarySubStep?: string;
  setSummarySubStep?: (label: string | undefined) => void;
}

const InterviewFlowContext = createContext<InterviewFlowContextType | undefined>(undefined);

export const InterviewFlowProvider = ({ children }: { children: ReactNode }) => {
  const { i18n, t } = useTranslation();
  const { interview, handleBackToOnboardingWithReset } = useInterviewContext();
  const { user } = useUserContext();
  const { isUserSpeaking, isAssistantSpeaking } = useMicdropState();
  const { isDevNoInterviewEnabled } = useBooth();
  const [step, setStep] = useState(INTERVIEW_STEPS.INTRO);
  const [generatedCV, setGeneratedCV] = useState<GeneratedCV | undefined>();
  const [lastMessage, setLastMessage] = useState<string | undefined>();
  const [isCVGenerating, setIsCVGenerating] = useState(false);
  const [summarySubStep, setSummarySubStep] = useState<string | undefined>();
  const restartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('speaking-activity'));

    let intervalId: NodeJS.Timeout | null = null;

    if (isUserSpeaking || isAssistantSpeaking) {
      intervalId = setInterval(() => {
        window.dispatchEvent(new CustomEvent('speaking-activity'));
      }, 10000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isUserSpeaking, isAssistantSpeaking]);

  const startInterview = (simulationScriptId?: string) => {
    if (!user || !interview) {
      const missing = [(!user && 'user') || null, (!interview && 'interview') || null].filter(
        Boolean,
      );
      console.error('[InterviewFlow] Cannot start: missing', missing.join(', '));
      toast.error(t('errors.invalid_interview_session'));
      handleBackToOnboardingWithReset();
      return;
    }
    const url = resolveWsUrl();
    url.pathname = '/call';

    setGeneratedCV(undefined);
    setSummarySubStep(undefined);
    setStep(INTERVIEW_STEPS.INTERVIEW);

    const vadConfig = ['silero', 'volume'] as const;
    console.warn(
      `[Micdrop][Connecting] Démarrage connexion vers ${url.toString()} (locale: ${i18n.language})`,
    );
    console.warn(
      `[Micdrop][Models] Modèle VAD demandé: [${vadConfig.join(', ')}] (Silero VAD v5 embarqué)`,
    );

    Micdrop.start({
      url: url.toString(),
      params: {
        userId: user.id,
        interviewId: interview.id,
        locale: i18n.language,
        ...(simulationScriptId ? { simulationScriptId } : {}),
      },
      vad: ['silero', 'volume'],
      disableInterruption: true,
      debugLog: true,
    }).catch(err => {
      console.error('[Micdrop][Start Error] Impossible de démarrer Micdrop:', err);
    });
  };

  const endInterview = () => {
    Micdrop.mute();

    if (Speaker.isPlaying) {
      setStep(INTERVIEW_STEPS.PHOTO);
      Micdrop.stop();
    } else {
      Speaker.once('StopPlaying', () => {
        setStep(INTERVIEW_STEPS.PHOTO);
        Micdrop.stop();
      });
    }
  };

  const goToSummary = () => {
    setStep(INTERVIEW_STEPS.SUMMARY);
  };

  const completeDevInterview = () => {
    if (!isDevNoInterviewEnabled) return;
    void Micdrop.stop();
    setGeneratedCV(getCvExemple(i18n.language));
    setIsCVGenerating(false);
    setLastMessage(undefined);
    setStep(INTERVIEW_STEPS.SUMMARY);
  };

  const restartWithSimulation = (simulationScriptId: string) => {
    if (!user || !interview) {
      toast.error(t('errors.invalid_interview_session'));
      return;
    }
    const url = resolveWsUrl();
    url.pathname = '/call';

    // Stop existing connection first, then start fresh
    Micdrop.stop();
    // Clear any pending restart
    if (restartTimeoutRef.current) clearTimeout(restartTimeoutRef.current);
    restartTimeoutRef.current = setTimeout(() => {
      console.warn(
        `[Micdrop][Connecting] Redémarrage simulation vers ${url.toString()} (scriptId: ${simulationScriptId})`,
      );
      console.warn(
        '[Micdrop][Models] Modèle VAD demandé: [silero, volume] (Silero VAD v5 embarqué)',
      );
      Micdrop.start({
        url: url.toString(),
        params: {
          userId: user.id,
          interviewId: interview.id,
          locale: i18n.language,
          simulationScriptId,
        },
        vad: ['silero', 'volume'],
        disableInterruption: true,
        debugLog: true,
      }).catch(err => {
        console.error('[Micdrop][Start Error] Impossible de redémarrer Micdrop:', err);
      });
    }, 1000);
  };

  useEffect(
    () => () => {
      if (restartTimeoutRef.current) clearTimeout(restartTimeoutRef.current);
      Micdrop.stop();
    },
    [],
  );

  // Silero VAD pre-flight warmup: pre-instantiate local ONNX session to avoid latency on start
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const warmup = new OnnxSileroModel();
        await warmup.load();
        if (active) {
          console.warn(
            '[Micdrop][VAD Warmup] Silero VAD v5 prêt (modèle & WebAssembly chargés en mémoire)',
          );
        }
        await warmup.release();
      } catch (err) {
        console.error('[Micdrop][VAD Warmup Error] Échec préchargement Silero VAD:', err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let prevMessage: MicdropConversationMessage | undefined = undefined;

    const handleError = (error: unknown) => {
      console.error('[Micdrop][Error]', error);
    };

    const handleStateChange = async (state: MicdropState, prevState?: MicdropState) => {
      // Log connection status transitions
      if (prevState && prevState.isStarted !== state.isStarted) {
        console.warn(
          `[Micdrop][Connection] isStarted: ${prevState.isStarted} -> ${state.isStarted}`,
        );
      }
      if (prevState && prevState.isReconnecting !== state.isReconnecting) {
        console.warn(
          `[Micdrop][Connection] isReconnecting: ${prevState.isReconnecting} -> ${state.isReconnecting}`,
        );
      }
      if (state.error && state.error !== prevState?.error) {
        console.error('[Micdrop][State Error]', state.error);
      }

      const message = state.conversation.findLast(({ role }) => role === 'assistant') as
        MicdropConversationMessage | undefined;
      if (message === prevMessage || !message) return;
      prevMessage = message;

      // Log the name of stt-llm-tts used
      if (message.metadata) {
        const stt = (message.metadata.stt ||
          message.metadata.sttProvider ||
          message.metadata.sttModel) as string | undefined;
        const llm = (message.metadata.model ||
          message.metadata.llm ||
          message.metadata.provider) as string | undefined;
        const tts = (message.metadata.tts ||
          message.metadata.ttsProvider ||
          message.metadata.ttsModel) as string | undefined;
        if (stt || llm || tts) {
          console.warn(`[Micdrop][Models] ${stt || 'N/A'}-${llm || 'N/A'}-${tts || 'N/A'}`);
        }
      }

      const short = message.metadata?.extracted?.short as string | undefined;
      const complete = message.metadata?.extracted?.complete as boolean | undefined;
      const cv = message.metadata?.cv as GeneratedCV | undefined;
      if (short) {
        setLastMessage(short);
      }
      if (complete) {
        setIsCVGenerating(true);
        Micdrop.mute();
      } else if (cv) {
        setGeneratedCV(cv);
        setIsCVGenerating(false);
        endInterview();
      } else {
        Micdrop.unmute();
        setIsCVGenerating(false);
      }
    };

    Micdrop.on('Error', handleError);
    Micdrop.on('StateChange', handleStateChange);

    return () => {
      Micdrop.off('Error', handleError);
      Micdrop.off('StateChange', handleStateChange);
    };
  }, []);

  const value: InterviewFlowContextType = {
    step,
    startInterview,
    restartWithSimulation,
    goToSummary,
    lastMessage,
    generatedCV,
    setGeneratedCV,
    isCVGenerating,
    completeDevInterview,
    summarySubStep,
    setSummarySubStep,
  };

  return <InterviewFlowContext.Provider value={value}>{children}</InterviewFlowContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useInterviewFlowContext = () => {
  const context = useContext(InterviewFlowContext);
  if (context === undefined) {
    throw new Error('useInterviewFlowContext must be used within an InterviewFlowProvider');
  }
  return context;
};

/** Safe variant — returns null when used outside InterviewFlowProvider (e.g. from FeedbackFab). */
// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalInterviewFlowContext = () => {
  return useContext(InterviewFlowContext) ?? null;
};
